import React, { useEffect, useRef } from 'react';
import classes from './Home8.module.scss';
import webDevImg from './web-dev.png';
import appDevImg from './app-dev.png';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Home8 = () => {
  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const block1Ref = useRef(null);
  const block2Ref = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let ctx = gsap.context(() => {
      
      // Hero Entrance Timeline
      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

      tl.from(`.${classes.logoImg}`, { opacity: 0, y: 30, duration: 1.5, delay: 0.2 })
        .from(`.${classes.titleLine}`, { 
          yPercent: 120, 
          duration: 1.5, 
          stagger: 0.15 
        }, '-=1.2')
        .from(`.${classes.subtitle}`, { 
          yPercent: 120, 
          opacity: 0, 
          duration: 1.5 
        }, '-=1.2')
        .to(`.${classes.scrollIndicator}`, { 
          opacity: 1, 
          duration: 1 
        }, '-=0.5');

      // Hero Logo Infinite Color <-> White Loop
      gsap.to(`.${classes.logoImg}`, {
        filter: 'brightness(1) invert(0)',
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
        delay: 1.5 // Start after entrance animation
      });

      // Hero Parallax Fade Out on Scroll
      gsap.to(`.${classes.heroContent}`, {
        yPercent: 30,
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        }
      });

      // Revealing Service Blocks
      const revealBlock = (blockRef) => {
        const el = blockRef.current;
        if (!el) return;

        const tlBlock = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        });

        // 1. Text elements slide up
        tlBlock.fromTo(el.querySelectorAll(`.${classes.serviceType}, .${classes.serviceTitle}, .${classes.serviceDesc}`),
          { yPercent: 100, opacity: 0 },
          { yPercent: 0, opacity: 1, duration: 1.2, stagger: 0.1, ease: 'power4.out' }
        );

        // 2. Image overlay reveals image
        const overlay = el.querySelector(`.${classes.imageRevealOverlay}`);
        if(overlay) {
          tlBlock.to(overlay, {
            scaleY: 0,
            duration: 1.2,
            ease: 'expo.inOut'
          }, '<'); // Start at the same time as text
        }

        // 3. Image scales down to 1 slowly via scrub to create parallax effect
        const img = el.querySelector(`.${classes.serviceImage}`);
        if(img) {
          gsap.to(img, {
            scale: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true
            }
          });
        }
      };

      revealBlock(block1Ref);
      revealBlock(block2Ref);

      // Master Color Transition on Scroll (Dark to Light)
      const colorTl = gsap.timeline({
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top top',
          endTrigger: block1Ref.current,
          end: 'top top', // Ends when the top of the Web Development block hits the top of the screen
          scrub: true
        }
      });

      colorTl.to(rootRef.current, {
        '--bg-color': '#f8fafc',
        '--text-main': '#0f2942',
        '--text-title': '#0f2942',
        '--text-sub': '#64748b',
        '--text-desc': '#334155',
        '--text-muted': '#64748b',
        '--text-copyright': '#94a3b8',
        '--accent-color': '#0ea5e9',
        '--line-color': 'rgba(15, 41, 66, 0.2)',
        '--border-color': 'rgba(15, 41, 66, 0.1)',
        ease: 'none'
      }, 0);

      // Restore footer logo from white mask to original colors on scroll
      colorTl.to(`.${classes.footerLogoImg}`, {
        filter: 'brightness(1) invert(0)',
        opacity: 1,
        ease: 'none'
      }, 0);

      // Remove grayscale from images as we scroll down
      colorTl.to(`.${classes.serviceImage}`, {
        filter: 'grayscale(0%) contrast(1)',
        ease: 'none'
      }, 0);

      // Fade out background glow
      colorTl.to(`.${classes.bgGlow}`, {
        opacity: 0,
        ease: 'none'
      }, 0);

    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className={classes.root} ref={rootRef}>
      <div className={classes.bgGlow}></div>
      
      {/* Hero Section */}
      <section className={classes.hero} ref={heroRef}>
        <div className={classes.heroContent}>
          <div className={classes.companyName}>
            <img src="/react-assets/logo.svg" alt="collabo Inc." className={classes.logoImg} />
          </div>
          <div className={classes.titleWrapper}>
            <div className={classes.titleLineWrapper}>
              <h1 className={classes.titleLine}>Design the</h1>
            </div>
            <div className={classes.titleLineWrapper}>
              <h1 className={classes.titleLine}>
                <span className={classes.highlight}>Future</span>
              </h1>
            </div>
          </div>
          <div className={classes.subtitleWrapper}>
            <p className={classes.subtitle}>
              私たちは、社内の業務管理・顧客管理システムを<br />
              AI活用で自動化し、各社向けに開発しています。
            </p>
          </div>
        </div>

        <div className={classes.scrollIndicator}>
          <span className={classes.scrollText}>Scroll</span>
          <div className={classes.scrollLine}>
            <div className={classes.scrollThumb}></div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className={classes.detailSection}>
        
        {/* Business System Development */}
        <div className={classes.serviceBlock} ref={block1Ref}>
          <div className={classes.serviceText}>
            <div className={classes.serviceTypeWrapper}>
              <div className={classes.serviceType}>Service 01 / Business System</div>
            </div>
            <div className={classes.serviceTitleWrapper}>
              <h3 className={classes.serviceTitle}>業務管理・顧客管理システム開発</h3>
            </div>
            <div className={classes.serviceDescWrapper}>
              <p className={classes.serviceDesc}>
                社内業務と顧客対応のフローを整理し、
                現場で使いやすい管理システムを設計・開発します。
                画面、権限、通知まで、自社運用に合わせてカスタマイズ可能です。
              </p>
            </div>
          </div>
          <div className={classes.serviceImageWrapper}>
            <div className={classes.imageRevealOverlay}></div>
            <img src={webDevImg} alt="業務管理・顧客管理システム開発" className={classes.serviceImage} />
          </div>
        </div>

        {/* App Development */}
        <div className={`${classes.serviceBlock} ${classes.reverse}`} ref={block2Ref}>
          <div className={classes.serviceText}>
            <div className={classes.serviceTypeWrapper}>
              <div className={classes.serviceType}>Service 02 / App Solution</div>
            </div>
            <div className={classes.serviceTitleWrapper}>
              <h3 className={classes.serviceTitle}>アプリをつくる。育てる。届ける。</h3>
            </div>
            <div className={classes.serviceDescWrapper}>
              <p className={classes.serviceDesc}>
                企画・設計・開発から公開後の改善まで、一気通貫で対応します。
                社内業務向けにも顧客向けにも、運用に合わせたアプリを提供します。
                必要に応じてAIを組み込み、定型業務の自動化まで実装します。
                補助金活用を前提にした開発相談にも対応可能です
                （採択を保証するものではありません）。
              </p>
            </div>
          </div>
          <div className={classes.serviceImageWrapper}>
            <div className={classes.imageRevealOverlay}></div>
            <img src={appDevImg} alt="アプリ開発" className={classes.serviceImage} />
          </div>
        </div>
        
      </section>

      <footer className={classes.footer}>
        <div className={classes.footerInner}>
          <div className={classes.footerLogo}>
             <img src="/react-assets/logo.svg" alt="collabo Inc." className={classes.footerLogoImg} />
          </div>
          <div className={classes.footerAddress}>
            〒106-0032 東京都港区六本木X-X-X<br/>
            TEL: 03-XXXX-XXXX<br/>
            Email: contact@collabo.example.com
          </div>
          <p className={classes.copyright}>&copy; {new Date().getFullYear()} collabo Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home8;
